import styled from '@emotion/styled';
import Modal from 'react-modal';
import { themeGet } from '@styled-system/theme-get';
import withFixedOverlay from '../../withFixedOverlay';
import StyledCart from '../Cart/styles';
import { StyledPlanColumn, StyledPlanRow } from '../PlanComparison/styles';
import StyledTooltip from '../Tooltip/styles';

const ModalWithFixedOverlay = withFixedOverlay(Modal);

const StyledModal = styled(ModalWithFixedOverlay)`
  width: 100%;
  max-width: ${(props) =>
    props.isWide ? '1060px' : themeGet('breakpoint.breakpointMobile')};
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 0;
  outline: none;
  margin: auto;

  &::before,
  &::after {
    content: '';
    display: block;
    height: 10vh;

    @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
      display: none;
    }
  }

  @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
    height: 100vh;
  }

  > div {
    background-color: ${themeGet('color.colorBlueLightest')};
    padding: 0 ${themeGet('scale.scaleUp03')} ${themeGet('scale.scaleUp03')};
  }

  @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
    height: 100vh;
    padding-bottom: 144px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .closer {
    position: absolute;
    top: ${themeGet('scale.scaleUp03')};
    right: ${themeGet('scale.scaleUp03')};
    padding: 0;
    background: transparent;
    border: 0;
    -webkit-appearance: none;

    &:hover {
      cursor: pointer;
    }
  }

  .modal-controls {
    padding: ${themeGet('scale.scaleUp03')} 0 0;
    display: flex;

    h3 {
      margin: 0;
    }

    button {
      padding: 0;
      background: transparent;
      border: 0;
      -webkit-appearance: none;
      margin-left: auto;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .modal-title {
    background: ${themeGet('color.colorBlueLightest')};
    transition: box-shadow 0.2s linear;

    &.is-sticky {
      position: sticky;
      top: 0;
      z-index: ${themeGet('zIndex.zIndex01')};

      .modal-controls {
        padding: ${themeGet('scale.scaleUp03')} 0;
      }
    }

    &.is-stuck {
      box-shadow: 0 10px 15px -15px rgba(0, 0, 0, 0.25);
    }
  }

  .modal-footer {
    color: ${themeGet('colorcolorGrayBlue70')};

    @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      padding-top: ${themeGet('scale.scaleUp03')};
      padding-bottom: ${themeGet('scale.scaleUp01')};
      background-color: ${themeGet('color.colorWhite')};
    }
  }

  ${StyledCart} {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;

    > div {
      flex: 1;
    }

    @supports (display: grid) {
      display: grid;
      grid-template-columns: 2fr 1fr;
      grid-gap: ${themeGet('scale.scaleBase')};
      align-items: stretch;

      > div {
        flex: 0;
      }
    }

    .cart-items {
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: ${themeGet('scale.scaleUp01')} ${themeGet('scale.scaleUp03')};
      background-color: rgba(223, 233, 250, 0.5);
      border-radius: ${themeGet('border.borderRadius')};
    }

    .plans {
      width: 100%;
      font-size: ${themeGet('scale.scaleDown01')};

      @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
        grid-template-columns: 1fr;
      }
    }

    .plans-label,
    .addons-label {
      color: ${themeGet('color.colorGrayBlue70')};

      @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
        grid-template-columns: 1fr;
        margin-bottom: ${themeGet('scale.scaleDown04')};
      }
    }

    .addons {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      margin-top: ${themeGet('scale.scaleDown03')};
      padding-top: ${themeGet('scale.scaleDown03')};
      font-size: ${themeGet('scale.scaleDown01')};
      border-top: ${themeGet('border.borderSmall')} solid
        ${themeGet('color.colorGrayBlue40')};

      > div {
        flex: 1;
      }

      @supports (display: grid) {
        display: grid;
        grid-template-columns: 100px auto;
        grid-column-gap: ${themeGet('scale.scaleUp03')};
        width: 100%;

        > div {
          flex: 0;
        }

        @media (max-width: ${themeGet('breakpoint.breakpointMobile')}) {
          grid-template-columns: 1fr;
          border-top: 0;
        }
      }
    }

    .addons-packages {
      font-family: "Whitney SSm A", "Whitney SSm B", "Helvetica Neue", Helvetica, Arial, sans-serif;
      font-weight: ${themeGet('fontWeight.fontWeightBold')};
      color: ${themeGet('color.colorGrayBlue100')};

      ${StyledTooltip} {
        [role='button'] {
          color: ${themeGet('color.colorBlue')};
        }
      }
    }

    .total {
      display: flex;
      flex-direction: column;
      justify-content: center;
      width: 100%;
      padding: ${themeGet('scale.scaleUp01')} ${themeGet('scale.scaleUp03')};
      font-size: ${themeGet('scale.scaleDown01')};
      text-align: center;
      color: ${themeGet('color.colorGrayBlue70')};
      background-color: rgba(223, 233, 250, 0.5);
      border-radius: ${themeGet('border.borderRadius')};

      span {
        display: block;
        margin-right: 0;
      }

      strong {
        font-size: ${themeGet('scale.scaleUp02')};
        color: ${themeGet('color.colorGrayBlue100')};
      }

      small {
        display: none;
      }
    }
  }

  ${StyledPlanRow} {
    color: ${themeGet('color.colorBlack')};

    &.plan-row-header {
      padding: 0;
      background-color: ${themeGet('color.colorGrayBlue20')};
      border-bottom: 0;
      z-index: ${themeGet('zIndex.zIndex01')};

      ${StyledPlanColumn} {
        padding: ${themeGet('scale.scaleBase')};
        border-bottom: 2px solid ${themeGet('color.colorGrayBlue40')};
        border-left: 2px solid ${themeGet('color.colorGrayBlue40')};

        &:first-of-type {
          border-left: 0;
        }
      }

      .download {
        position: relative;
        top: -10px;

        &::after {
          content: '⇣';
        }
      }
    }
  }

  ${StyledPlanColumn} {
    min-width: 160px;
    color: ${themeGet('color.colorBlack')};

    ${StyledTooltip} .tooltip-text {
      border-color: ${themeGet('color.colorBlack')};
    }

    .check-icon svg path {
      fill: ${themeGet('color.colorTeal')};
    }
  }

  ${StyledTooltip} {
    [role='tooltip'] {
      z-index: ${themeGet('zIndex.zIndex02')};
    }

    [role='tooltip']::after {
      border-color: ${themeGet('color.colorGrayBlue100')} transparent
        transparent transparent;
    }

    [role='tooltip'] > div {
      color: ${themeGet('color.colorWhite')};
      background-color: ${themeGet('color.colorGrayBlue100')};
    }
  }
`;

export default StyledModal;
